// src/pages/Checkout.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI, promoAPI, settingsAPI, dealAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiCreditCard, FiLock, FiUpload, FiCopy, FiCheckCircle, FiMinus, FiPlus, FiFileText, FiTrash2 } from 'react-icons/fi';
import { FaUniversity, FaMoneyBillWave } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import { useCart } from '../context/CartContext';
import { feeFromWeight } from '../utils/shipping';
import PayExpressBadge from '../components/payments/PayExpressBadge';
import PaymentBrandLogos, { CARD_BRANDS, CASH_AGENCY_BRANDS } from '../components/payments/PaymentBrandLogos';
import { cartItemTotal, cartItemUnitPrice } from '../utils/quantityTiers';

// ----------------------------------------------------------------------------
// RIB : ce qu'on AFFICHE et ce qu'on COPIE sont deux choses differentes.
//
// A l'ecran, des groupes de 4 chiffres se lisent et se verifient facilement.
// Dans le presse-papier, la moindre espace ou retour a la ligne fait rejeter le
// numero par l'application bancaire au moment de creer le beneficiaire — et le
// client croit alors que le RIB est faux.
// ----------------------------------------------------------------------------
const ribForCopy = (v) => String(v || '').replace(/[\s\u00A0\u202F-]/g, '');

const ribForDisplay = (v) => {
  const raw = ribForCopy(v);
  // Regroupement par 4 UNIQUEMENT si c'est une suite de chiffres : un IBAN
  // etranger ou une saisie libre doivent rester affiches tels quels.
  if (!/^\d{8,34}$/.test(raw)) return String(v || '');
  return raw.replace(/(.{4})/g, '$1 ').trim();
};

const Checkout = () => {
  const { user, applyAuth } = useAuth() || {};
  const { clearCart: clearCartCtx, removeFromCart: removeFromCartCtx, updateQuantity: updateQuantityCtx } = useCart();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  // Virement bancaire : infos commande créée + RIB plateforme + reçu
  const [virementInfo, setVirementInfo] = useState(null); // { orderId, orderNumber, total, guestPaymentToken, hasSession }
  const [bank, setBank] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofSending, setProofSending] = useState(false);

  // Payzone Cash (espèces en agence) : disponibilité + infos réservation
  const [cashAvailable, setCashAvailable] = useState(false);
  const [cashMinAmount, setCashMinAmount] = useState(0); // seuil min « espèces en agence » (admin)
  const [cashInfo, setCashInfo] = useState(null); // { orderId, orderNumber, token, receiptUrl, amount, hasSession }
  const proofInputRef = useRef(null);

  // Guest checkout (commande sans compte) : champs saisis si non connecté
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  // Adresse de livraison (produits physiques)
  const [shipping, setShipping] = useState({
    name: '', address: '', apt: '', city: '', state: '', postalCode: '', country: 'MA', phone: ''
  });
  const setShip = (k, v) => setShipping((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (paymentMethod !== 'virement' || bank) return;
    (async () => {
      try {
        const r = await settingsAPI.get();
        const s = r?.data?.data || r?.data || {};
        setBank(s.bankTransfer || s.settings?.bankTransfer || {});
      } catch (e) { setBank({}); }
    })();
  }, [paymentMethod, bank]);

  // Disponibilité du paiement « espèces en agence » (Payzone Cash).
  useEffect(() => {
    let mounted = true;
    API.get('/payment/cash/availability')
      .then((r) => { if (mounted) { const d = (r && r.data && r.data.data) || {}; setCashAvailable(!!d.available); setCashMinAmount(Number(d.minAmount) || 0); } })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Marqueur de version, lisible en console : `window.__MAXULA_CHECKOUT`.
  // Sert a repondre en deux secondes a « le build est-il bien en ligne ? »,
  // question qui a coute plusieurs allers-retours de diagnostic.
  useEffect(() => {
    try { window.__MAXULA_CHECKOUT = '1.439.0'; } catch (e) { /* noop */ }
  }, []);

  const loadCart = useCallback(() => {
    try {
      const raw = localStorage.getItem('cart') || '[]';
      const items = JSON.parse(raw);

      if (!Array.isArray(items) || items.length === 0) {
        toast.error('Votre panier est vide');
        navigate('/cart');
        return;
      }

      const normalized = items.map((it, idx) => {
        const dealId =
          it.dealId && typeof it.dealId === 'object'
            ? it.dealId._id || it.dealId.id
            : it.dealId || it._id || it.id;

        return {
          _localKey: it._localKey || `${dealId || 'x'}-${idx}`,
          dealId,
          quantity: Number(it.quantity) || 1,
          maxQuantity: Number(it.maxQuantity || it.availableQuantity || it.stock || it.quantityAvailable) || null,
          price: Number(it.price) || 0,
          // PRIX DEGRESSIF — NE JAMAIS RETIRER CES DEUX LIGNES.
          // Cette fonction RECONSTRUIT la ligne de panier champ par champ : tout
          // champ non recopie ici est PERDU, meme s'il est present dans le
          // localStorage. `basePrice` et `quantityTiers` manquaient, donc
          // cartItemUnitPrice() ne trouvait aucun palier et facturait le prix
          // plein a l'ecran — alors que le serveur, lui, appliquait bien la
          // remise. Ecran et debit ne concordaient pas.
          basePrice: Number(it.basePrice ?? it.price) || 0,
          quantityTiers: Array.isArray(it.quantityTiers) ? it.quantityTiers : null,
          merchantId: (it.merchantId && typeof it.merchantId === 'object'
            ? (it.merchantId.id || it.merchantId._id)
            : it.merchantId) || null,
          title: it.title || (it.dealId && it.dealId.title) || 'Deal',
          image: it.image || (it.dealId && it.dealId.images && it.dealId.images[0]) || '',
          // Marketplace multi-types
          productType: it.productType || (it.dealId && it.dealId.productType) || null,
          shippingFee: Number(it.shippingFee) || 0,
          freeShippingThreshold: (it.freeShippingThreshold != null && it.freeShippingThreshold !== '') ? Number(it.freeShippingThreshold) : null,
          shippingZones: Array.isArray(it.shippingZones) ? it.shippingZones : [],
          // Lot D — tarif au poids
          shippingMode: it.shippingMode === 'weight' ? 'weight' : 'flat',
          weightGrams: Number(it.weightGrams) || 0,
          shippingWeightTiers: Array.isArray(it.shippingWeightTiers) ? it.shippingWeightTiers : [],
          variantLabel: it.variantLabel || null,
          // Lieu choisi sur la fiche produit (annonce multi-villes). Sans cette
          // reprise, le choix serait perdu entre le panier et la commande, et
          // le coupon repartirait sur l'adresse de la ville principale.
          locationId: it.locationId || null,
          locationLabel: it.locationLabel || null
        };
      });

      setCartItems(normalized);
      setSelectedKeys(normalized.map((it) => it._localKey));

      // ====================================================================
      // RESYNCHRONISATION AVEC LE SERVEUR — TOUTES LES LIGNES, SANS EXCEPTION
      // ====================================================================
      // La page de paiement ne fait plus confiance au panier enregistre dans le
      // navigateur pour les PALIERS. Elle les relit sur l'annonce a chaque
      // ouverture.
      //
      // Pourquoi : un panier est un instantane, une annonce vit. Entre l'ajout
      // au panier et le paiement, le marchand peut avoir cree, modifie ou
      // supprime ses paliers. Le serveur, lui, facture toujours l'etat ACTUEL
      // de l'annonce. Toute divergence se voit directement sur le montant
      // debite.
      //
      // La version precedente ne rechargeait que les lignes ou `quantityTiers`
      // etait ABSENT. Elle laissait passer le cas le plus courant : une ligne
      // ajoutee au panier AVANT que le marchand ne cree ses paliers porte
      // `quantityTiers: []` — un tableau vide, donc bien un tableau, donc
      // considere comme valide et jamais rafraichi. Le prix plein restait
      // affiche indefiniment, y compris apres avoir re-clique sur « Acheter ».
      // C'est exactement ce qui s'est produit sur l'annonce 753.
      //
      // Le cout est d'une requete par annonce distincte, une seule fois a
      // l'ouverture de la page. C'est le bon endroit pour la depenser : c'est
      // la page ou un ecart entre l'affichage et le debit est le plus grave.
      const byDealId = new Map();
      normalized.forEach((it) => {
        if (it.dealId && !byDealId.has(String(it.dealId))) byDealId.set(String(it.dealId), it);
      });
      const missing = [...byDealId.values()];
      if (missing.length) {
        Promise.all(missing.map(async (it) => {
          try {
            let res;
            // MySQL : ids numériques -> /deals/id/:id ; repli slug si besoin.
            try { res = await API.get(`/deals/id/${encodeURIComponent(it.dealId)}`); }
            catch (_) { res = await dealAPI.getOne(it.dealId); }
            const d = (res && res.data && (res.data.data || res.data)) || {};
            const mid = d.merchantId && typeof d.merchantId === 'object'
              ? (d.merchantId.id || d.merchantId._id)
              : d.merchantId;
            return {
              dealId: String(it.dealId),
              merchantId: mid || it.merchantId || null,
              productType: d.productType || it.productType || 'service',
              shippingFee: Number(d.shippingFee) || 0,
              freeShippingThreshold: (d.freeShippingThreshold != null && d.freeShippingThreshold !== '') ? Number(d.freeShippingThreshold) : null,
              shippingZones: Array.isArray(d.shippingZones) ? d.shippingZones : [],
              shippingMode: d.shippingMode === 'weight' ? 'weight' : 'flat',
              weightGrams: Number(d.weightGrams) || 0,
              shippingWeightTiers: Array.isArray(d.shippingWeightTiers) ? d.shippingWeightTiers : [],
              // Paliers : l'annonce fait FOI, on remplace sans condition.
              // On ne touche en revanche PAS a `price` / `basePrice` : le prix
              // de la ligne inclut deja l'eventuel delta de variante, que
              // `d.discountedPrice` ignore — l'ecraser ferait payer le prix de
              // base sur une variante plus chere.
              quantityTiers: Array.isArray(d.quantityTiers) ? d.quantityTiers : []
            };
          } catch (e) { return null; }
        })).then((results) => {
          const map = {};
          // Indexe par ANNONCE et non par ligne : deux lignes du meme produit
          // (deux variantes) doivent recevoir les memes paliers avec une seule
          // requete.
          results.filter(Boolean).forEach((r) => { map[r.dealId] = r; });
          if (Object.keys(map).length) {
            setCartItems((prev) => {
              const merged = prev.map((it) => {
                const fields = map[String(it.dealId)];
                if (!fields) return it;
                // Les infos deja presentes sur la ligne priment (elles peuvent
                // inclure une variante) ; seuls les paliers sont ecrases.
                return {
                  ...it,
                  merchantId: it.merchantId || fields.merchantId,
                  productType: it.productType || fields.productType,
                  shippingFee: it.shippingFee || fields.shippingFee,
                  freeShippingThreshold: it.freeShippingThreshold != null ? it.freeShippingThreshold : fields.freeShippingThreshold,
                  shippingZones: (Array.isArray(it.shippingZones) && it.shippingZones.length) ? it.shippingZones : fields.shippingZones,
                  shippingMode: it.shippingMode || fields.shippingMode,
                  weightGrams: it.weightGrams || fields.weightGrams,
                  shippingWeightTiers: (Array.isArray(it.shippingWeightTiers) && it.shippingWeightTiers.length) ? it.shippingWeightTiers : fields.shippingWeightTiers,
                  quantityTiers: fields.quantityTiers
                };
              });
              // On reecrit le panier enrichi : sans cela la page /cart, qui lit
              // le meme localStorage, continuerait d'afficher le prix plein sur
              // ces anciennes lignes a chaque visite.
              try { localStorage.setItem('cart', JSON.stringify(merged)); } catch (e) { /* quota */ }
              return merged;
            });
          }
        });
      }
    } catch (err) {
      console.error('loadCart parse error', err);
      toast.error('Impossible de charger le panier');
      navigate('/cart');
    }
  }, [navigate]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const selectedItems = cartItems.filter((item) => selectedKeys.includes(item._localKey));

  // Le sous-total passe par le calcul partage des paliers : afficher le prix
  // plein ici alors que le serveur facturera la remise donnerait un total
  // different du montant debite — c'est le pire defaut sur une page de paiement.
  const subtotal = selectedItems.reduce((acc, item) => acc + cartItemTotal(item), 0);
  const discount = appliedPromo ? (Number(appliedPromo.discount) || 0) : 0;

  // ===== Produits physiques : détection + frais de livraison =====
  const hasPhysical = selectedItems.some((i) => i.productType === 'physical');
  const shippingFee = (() => {
    const normCity = (s) => String(s || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const buyerCity = normCity(shipping.city);
    const byDeal = {};
    selectedItems.forEach((i) => {
      if (i.productType !== 'physical') return;
      const k = String(i.dealId);
      if (!byDeal[k]) byDeal[k] = {
        fee: Number(i.shippingFee) || 0,
        threshold: i.freeShippingThreshold,
        zones: Array.isArray(i.shippingZones) ? i.shippingZones : [],
        line: 0,
        mode: i.shippingMode === 'weight' ? 'weight' : 'flat',
        weightGrams: Number(i.weightGrams) || 0,
        tiers: Array.isArray(i.shippingWeightTiers) ? i.shippingWeightTiers : [],
        qty: 0
      };
      byDeal[k].line += (Number(i.price) || 0) * (Number(i.quantity) || 0);
      byDeal[k].qty += (Number(i.quantity) || 0);
    });
    let fee = 0;
    Object.values(byDeal).forEach((d) => {
      let f = d.fee;
      let threshold = d.threshold;
      let zone = null;
      if (buyerCity && d.zones.length) {
        zone = d.zones.find((z) => Array.isArray(z.cities) && z.cities.some((c) => normCity(c) === buyerCity));
      }
      if (d.mode === 'weight') {
        // Tarif au poids : poids total = poids unitaire × quantité du deal.
        f = feeFromWeight(d.tiers, d.weightGrams * d.qty, d.fee);
        if (zone && zone.freeThreshold != null && zone.freeThreshold !== '') threshold = Number(zone.freeThreshold);
      } else if (zone) {
        f = Number(zone.fee) || 0;
        threshold = (zone.freeThreshold != null && zone.freeThreshold !== '') ? Number(zone.freeThreshold) : threshold;
      }
      const free = threshold != null && d.line >= threshold;
      if (!free) fee += f;
    });
    return Math.round(fee * 100) / 100;
  })();

  const total = Math.max(0, Math.round((subtotal - discount + shippingFee) * 100) / 100);

  // Si « espèces » devient indisponible (option masquée ou panier sous le seuil),
  // on repasse automatiquement sur la carte pour éviter une soumission invalide.
  // NB: placé APRÈS la déclaration de `total` (évite le ReferenceError / temporal dead zone).
  useEffect(() => {
    if (paymentMethod === 'cash' && (!cashAvailable || total < cashMinAmount)) setPaymentMethod('card');
  }, [paymentMethod, cashAvailable, cashMinAmount, total]);


  // Adresse de livraison complète ? (requise si panier physique)
  // VILLES A TARIF PARTICULIER.
  //
  // Le tarif d'une zone ne s'applique que si la ville saisie correspond
  // EXACTEMENT a l'une de celles de la zone (comparaison sans accent ni
  // majuscule). Un acheteur qui tape « Tanger Maroc » ou « Tanjah » paie le
  // tarif par defaut sans jamais savoir qu'un tarif reduit existait.
  // On lui montre donc la liste, et un clic remplit le champ correctement.
  const zoneCities = (() => {
    const out = new Map();
    selectedItems.forEach((i) => {
      if (i.productType !== 'physical') return;
      (Array.isArray(i.shippingZones) ? i.shippingZones : []).forEach((z) => {
        (Array.isArray(z?.cities) ? z.cities : []).forEach((c) => {
          const nom = String(c || '').trim();
          if (!nom) return;
          const cle = nom.toLowerCase();
          const f = Number(z.fee);
          // Plusieurs articles peuvent proposer la meme ville a des tarifs
          // differents : on garde le plus bas, celui qu'on peut annoncer sans
          // se tromper vers le haut.
          if (!out.has(cle) || (Number.isFinite(f) && f < out.get(cle).fee)) {
            out.set(cle, { name: nom, fee: Number.isFinite(f) ? f : 0 });
          }
        });
      });
    });
    return [...out.values()].sort((a, b) => a.fee - b.fee);
  })();

  const shippingComplete = ['name', 'address', 'city', 'phone'].every((f) => String(shipping[f] || '').trim());

  // Modifier la quantité d'un article directement au checkout (+/-).
  // Met à jour l'affichage local + le contexte panier (badge + localStorage).
  const changeQty = (item, delta) => {
    const cur = Number(item.quantity) || 1;
    const next = cur + delta;
    if (next < 1) return; // ne descend pas sous 1
    const max = Number(item.maxQuantity) || 99;
    if (next > max) {
      if (item.maxQuantity) toast.error(`Stock disponible : ${item.maxQuantity}`);
      return;
    }
    setCartItems((prev) => prev.map((it) => (it._localKey === item._localKey ? { ...it, quantity: next } : it)));
    try { if (item.dealId) updateQuantityCtx(item, next); } catch (e) {}
  };

  // RETIRER UNE LIGNE DU PANIER.
  //
  // `removeFromCart` existait deja dans le contexte, mais aucun bouton ne
  // l'appelait depuis cet ecran : on pouvait decocher un article sans jamais
  // pouvoir le supprimer, et il restait a l'ecran commande apres commande.
  //
  // Deux etats a tenir a jour, pas un : la liste LOCALE de l'ecran et le
  // panier du CONTEXTE (conserve entre les pages). N'en mettre qu'un a jour
  // ferait reapparaitre l'article au rechargement.
  const removeLine = (item) => {
    setCartItems((prev) => prev.filter((it) => it._localKey !== item._localKey));
    setSelectedKeys((prev) => prev.filter((k) => k !== item._localKey));
    try { removeFromCartCtx(item); } catch (e) {}
  };

  const allSelected = cartItems.length > 0 && selectedKeys.length === cartItems.length;

  const applyPromo = async () => {
    const code = (promoInput || '').trim();
    if (!code) return;
    if (subtotal <= 0) { toast.error('Sélectionnez d\'abord des articles'); return; }
    setPromoLoading(true);
    try {
      const merchantIds = [...new Set(selectedItems.map((i) => i.merchantId).filter(Boolean))];
      const subtotalByMerchant = {};
      selectedItems.forEach((i) => {
        if (!i.merchantId) return;
        const k = String(i.merchantId);
        const price = Number(i.discountedPrice || i.price) || 0;
        subtotalByMerchant[k] = (subtotalByMerchant[k] || 0) + price * (Number(i.quantity) || 1);
      });
      const { data } = await promoAPI.apply({ code, subtotal, merchantIds, subtotalByMerchant });
      setAppliedPromo(data?.data || null);
      toast.success(`Code appliqué : -${(data?.data?.discount || 0).toFixed(2)} MAD`);
    } catch (e) {
      setAppliedPromo(null);
      toast.error(e?.response?.data?.message || 'Code promo invalide');
    } finally { setPromoLoading(false); }
  };
  const removePromo = () => { setAppliedPromo(null); setPromoInput(''); };


  const toggleItemSelection = (key) => {
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelectedKeys([]);
    else setSelectedKeys(cartItems.map((it) => it._localKey));
  };

  const submitGatewayForm = useCallback((paymentData) => {
    console.log('🔍 submitGatewayForm appelé avec:', paymentData);

    if (!paymentData || !paymentData.paymentUrl) {
      console.error('❌ paymentUrl manquante:', paymentData);
      toast.error('Erreur: URL de paiement manquante');
      return;
    }

    if (!paymentData.params || typeof paymentData.params !== 'object') {
      console.error('❌ params invalides:', paymentData);
      toast.error('Erreur: paramètres de paiement invalides');
      return;
    }

    try {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentData.paymentUrl;
      form.style.display = 'none';

      Object.entries(paymentData.params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value == null ? '' : String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('❌ Erreur lors de la création du formulaire:', error);
      toast.error('Erreur lors de la redirection vers le paiement');
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!cartItems || cartItems.length === 0) {
        toast.error('Panier vide');
        return;
      }

      if (!selectedItems || selectedItems.length === 0) {
        toast.error('Sélectionnez au moins un article à payer');
        return;
      }

      const isGuest = !user || !user.email;

      // Validation des coordonnées
      if (isGuest) {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim());
        if (!emailOk) {
          toast.error('Entrez un email valide pour commander');
          return;
        }
        if (!guestPhone || guestPhone.trim() === '') {
          toast.error('Entrez votre numéro de téléphone');
          return;
        }
      } else {
        // ✅ VÉRIFICATION TÉLÉPHONE OBLIGATOIRE (compte existant)
        if (!user.phone || user.phone.trim() === '') {
          toast.error('Veuillez ajouter votre numéro de téléphone dans votre profil avant de commander');
          navigate('/dashboard/profile');
          return;
        }
      }

      // Adresse de livraison obligatoire si le panier contient un produit physique
      if (hasPhysical && !shippingComplete) {
        toast.error("Complétez l'adresse de livraison (nom, adresse, ville, téléphone)");
        return;
      }

      setLoading(true);

      try {
        // 1) Créer commande
        const items = selectedItems.map((item) => ({
          dealId: item.dealId,
          quantity: Number(item.quantity) || 1,
          // Le serveur RECALCULE de toute facon (applyQuantityTiers) : ce prix
          // n'est qu'indicatif. On envoie le prix remise plutot que le prix
          // plein pour que les journaux concordent avec ce que le client a vu.
          price: cartItemUnitPrice(item),
          variantLabel: item.variantLabel || undefined,
          // Le serveur revalide ce lieu contre l'annonce avant de figer
          // l'adresse sur le coupon.
          locationId: item.locationId || undefined
        }));
        const promoCode = appliedPromo ? appliedPromo.code : undefined;
        const shippingPayload = hasPhysical ? { ...shipping } : undefined;

        let orderResponse;
        let guestPaymentToken = null;
        let accountToken = null;
        if (isGuest) {
          // Commande invité (style Groupon) : pas besoin de créer un compte.
          orderResponse = await orderAPI.createGuest({
            email: guestEmail.trim(),
            name: guestName.trim(),
            phone: guestPhone.trim(),
            items,
            paymentMethod,
            promoCode,
            shipping: shippingPayload
          });
          accountToken = orderResponse?.data?.token || null;                       // présent seulement si NOUVEAU compte créé
          guestPaymentToken = orderResponse?.data?.guestPaymentToken || null;      // jeton limité à cette commande
          const usr = orderResponse?.data?.data?.user;
          // Auto-login UNIQUEMENT si un nouveau compte vient d'être créé.
          // Jamais pour un email déjà existant (anti-account-takeover).
          if (accountToken && typeof applyAuth === 'function') applyAuth({ token: accountToken, user: usr });
        } else {
          orderResponse = await orderAPI.create({ items, paymentMethod, promoCode, shipping: shippingPayload });
        }

        if (!orderResponse?.data?.success) {
          throw new Error(orderResponse?.data?.message || 'Erreur création commande');
        }

        const responseData = orderResponse.data.data;
        const order = responseData.order || responseData;
        const orderId = order._id || order.id;

        if (!orderId) {
          console.error('❌ Structure de réponse:', orderResponse.data);
          throw new Error('ID de commande manquant');
        }

        console.log('✅ Commande créée:', orderId);

        // MAJ panier local
        const remainingItems = cartItems.filter((item) => !selectedKeys.includes(item._localKey));
        if (remainingItems.length > 0) localStorage.setItem('cart', JSON.stringify(remainingItems));
        else localStorage.removeItem('cart');

        setCartItems(remainingItems);
        setSelectedKeys(remainingItems.map((it) => it._localKey));

        // Synchroniser le panier GLOBAL (CartContext) : sinon les articles
        // commandes restent affiches partout (le contexte garde son etat en memoire).
        try {
          if (remainingItems.length === 0) {
            if (clearCartCtx) clearCartCtx();
          } else {
            const orderedItems = cartItems.filter((item) => selectedKeys.includes(item._localKey));
            orderedItems.forEach((it) => { if (it.dealId != null && removeFromCartCtx) removeFromCartCtx(it); });
          }
        } catch (e) {}

        // 2) Paiement
        if (paymentMethod === 'card') {
          toast.info('Redirection vers la page de paiement sécurisé...');

          const paymentResponse = await API.post('/payment/initiate', { orderId, guestPaymentToken });

          if (!paymentResponse?.data?.success) {
            throw new Error(paymentResponse?.data?.message || 'Erreur initialisation paiement');
          }

          const paymentData = paymentResponse.data.data;

          if (!paymentData?.paymentUrl || !paymentData?.params) {
            throw new Error('Données de paiement invalides reçues du serveur');
          }

          localStorage.setItem('pendingOrderId', orderId);

          setTimeout(() => submitGatewayForm(paymentData), 300);
          return;
        }

        // Virement bancaire : afficher les instructions RIB + upload du reçu
        if (paymentMethod === 'virement') {
          setVirementInfo({
            orderId,
            orderNumber: order.orderNumber || '',
            total: order.total || 0,
            guestPaymentToken: guestPaymentToken || null,
            hasSession: !isGuest || !!accountToken
          });
          toast.success('Commande créée ! Effectuez le virement puis envoyez votre reçu.');
          setLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }

        // Espèces en agence (Payzone Cash) : créer la réservation et afficher le code
        if (paymentMethod === 'cash') {
          try {
            const resaResp = await API.post('/payment/cash/reserve', { orderId, guestPaymentToken });
            const d = resaResp && resaResp.data && resaResp.data.data;
            if (!resaResp?.data?.success || !d?.token) {
              throw new Error(resaResp?.data?.message || 'Réservation en espèces impossible');
            }
            setCashInfo({
              orderId,
              orderNumber: order.orderNumber || '',
              token: d.token,
              receiptUrl: d.receiptUrl || null,
              amount: d.amount || order.total || 0,
              hasSession: !isGuest || !!accountToken,
            });
            toast.success('Réservation créée ! Payez en agence avec votre code.');
            setLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          } catch (e) {
            toast.error(e?.response?.data?.message || e.message || 'Réservation en espèces échouée');
            setLoading(false);
            return;
          }
        }

        // Paiement différé
        toast.success('Commande créée ! Paiement en attente.');
        if (isGuest && !accountToken) {
          // Invité sans session (email déjà rattaché à un compte) : pas d'accès au
          // tableau de bord protégé -> on renvoie à l'accueil. Coupon envoyé par email/WhatsApp.
          navigate('/');
        } else {
          navigate('/dashboard/orders');
        }
      } catch (error) {
        console.error('❌ Erreur checkout:', error);
        const code = error?.response?.data?.code;
        if (error?.response?.status === 409 || code === 'ACCOUNT_EXISTS') {
          toast.info('Un compte existe déjà avec cet email. Connectez-vous pour finaliser votre commande.');
          navigate('/login');
          setLoading(false);
          return;
        }
        const errorMessage =
          error?.response?.data?.message || error.message || 'Erreur lors du paiement';
        toast.error(errorMessage);
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartItems, selectedItems, paymentMethod, user, navigate, submitGatewayForm, selectedKeys, appliedPromo, applyAuth, guestEmail, guestName, guestPhone]
  );

  const submitDisabled = loading || selectedItems.length === 0 || (hasPhysical && !shippingComplete);

  // Envoi du reçu de virement
  const sendProof = async () => {
    if (!proofFile) { toast.warn('Choisissez d\'abord la photo / capture de votre reçu.'); return; }
    if (!virementInfo?.orderId) return;
    setProofSending(true);
    try {
      const fd = new FormData();
      fd.append('proof', proofFile);
      await orderAPI.uploadPaymentProof(virementInfo.orderId, fd, virementInfo.guestPaymentToken);
      toast.success('Reçu envoyé ! Votre paiement sera vérifié rapidement.');
      navigate(virementInfo.hasSession ? '/dashboard/orders' : '/');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'envoi du reçu');
    } finally {
      setProofSending(false);
    }
  };

  const copyText = (txt) => {
    try { navigator.clipboard.writeText(String(txt || '')); toast.info('Copié !'); } catch (e) {}
  };

  // Écran « espèces en agence » (après réservation Payzone Cash)
  if (cashInfo) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white rounded-2xl shadow p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
              <FaMoneyBillWave size={20} />
            </span>
            <h1 className="text-2xl font-bold">Réservation créée</h1>
          </div>
          <p className="text-gray-600 mb-4">
            Commande <span className="font-semibold">{cashInfo.orderNumber}</span> — présentez le code ci-dessous
            dans une agence <span className="font-semibold">Cash Plus, Barid Cash, Damane Cash ou Lana Cash</span>{' '}
            et réglez <span className="font-semibold">{Number(cashInfo.amount || 0).toFixed(2)} MAD</span> en espèces.
          </p>

          {/* Au guichet, l'agent ne connaît QUE l'enseigne PayExpress : « Payzone » est
              le nom de la passerelle technique et n'évoque rien pour lui. On l'affiche
              donc en évidence, avec la phrase exacte à prononcer. */}
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <PayExpressBadge size="md" />
              <span className="text-sm font-semibold text-amber-900">
                À dire à l'agent au guichet
              </span>
            </div>
            <p className="text-sm text-amber-900">
              Demandez un paiement <span className="font-bold">PAYEXPRESS</span>. C'est le nom
              affiché sur le terminal de l'agence. Ne dites pas « Payzone » : les agents ne
              connaissent pas ce nom et ne trouveront pas l'opération.
            </p>
          </div>

          <div className="border-2 border-dashed border-primary-300 rounded-xl p-5 text-center mb-6">
            <div className="text-sm text-gray-500 mb-1">Votre code de paiement</div>
            <div className="text-3xl font-extrabold tracking-wider text-primary-700 select-all">{cashInfo.token}</div>
            <button
              type="button"
              onClick={() => { try { navigator.clipboard.writeText(String(cashInfo.token)); toast.info('Code copié !'); } catch (e) {} }}
              className="mt-3 inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
            >
              <FiCopy /> Copier le code
            </button>
          </div>

          <ol className="list-decimal list-inside text-gray-700 space-y-1 mb-6 text-sm">
            <li>Rendez-vous dans une agence <strong>Cash Plus, Barid Cash, Damane Cash ou Lana Cash</strong>.</li>
            <li>Demandez un paiement <strong>PAYEXPRESS</strong> (c'est le service utilisé par l'agence).</li>
            <li>Communiquez votre code et réglez le montant en espèces.</li>
            <li>Votre commande sera confirmée automatiquement après le paiement.</li>
          </ol>

          {cashInfo.receiptUrl && (
            <a
              href={cashInfo.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-6"
            >
              <FiFileText /> Télécharger le reçu
            </a>
          )}

          <button
            type="button"
            onClick={() => navigate(cashInfo.hasSession ? '/dashboard/orders' : '/')}
            className="w-full bg-primary-600 text-white rounded-lg py-3 font-semibold hover:bg-primary-700"
          >
            {cashInfo.hasSession ? 'Voir mes commandes' : "Retour à l'accueil"}
          </button>
        </div>
      </div>
    );
  }

  // Écran instructions VIREMENT (après création de la commande)
  if (virementInfo) {
    const b = bank || {};
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white rounded-2xl shadow p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center">
              <FaUniversity size={20} />
            </span>
            <div>
              <h1 className="text-2xl font-bold">Payer par virement bancaire</h1>
              <p className="text-sm text-gray-600">Commande <span className="font-mono font-semibold">{virementInfo.orderNumber}</span></p>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-gray-200 divide-y">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-600">Montant à virer</span>
              <span className="font-bold text-primary-600">{Number(virementInfo.total || 0).toFixed(2)} MAD</span>
            </div>
            {b.accountHolder && (
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-600">Titulaire du compte</span>
                <span className="font-semibold">{b.accountHolder}</span>
              </div>
            )}
            {b.bankName && (
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm text-gray-600">Banque</span>
                <span className="flex items-center gap-2">
                  <span className="font-semibold">{b.bankName}</span>
                  {/* Fichier public/payment-logos/bank.png. Absent, une pastille
                      au nom de la banque s'affiche a la place. */}
                  <PaymentBrandLogos brands={[{ slug: 'bank', label: b.bankName }]} height="h-6" />
                </span>
              </div>
            )}
            {(b.rib || b.iban) && (
              <div className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm text-gray-600 shrink-0">RIB</span>
                  <span className="flex items-start gap-2 min-w-0">
                    <span className="font-mono font-semibold text-sm break-all text-right">
                      {ribForDisplay(b.rib || b.iban)}
                    </span>
                    <button
                      type="button"
                      onClick={() => copyText(ribForCopy(b.rib || b.iban))}
                      className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 shrink-0"
                      title="Copier le RIB sans espaces"
                    >
                      <FiCopy size={15} />
                    </button>
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  Cliquez sur l'icône pour copier le RIB (sans espaces).
                </p>
              </div>
            )}
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <span className="text-sm text-gray-600">Motif / Référence à indiquer</span>
              <span className="flex items-center gap-2">
                <span className="font-mono font-semibold">{virementInfo.orderNumber}</span>
                <button type="button" onClick={() => copyText(virementInfo.orderNumber)}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" title="Copier la référence">
                  <FiCopy size={15} />
                </button>
              </span>
            </div>
          </div>

          {b.instructions && (
            <p className="mt-3 text-sm text-gray-600 whitespace-pre-line">{b.instructions}</p>
          )}

          <div className="mt-6 border-2 border-dashed border-gray-300 rounded-xl p-5 text-center">
            <p className="font-semibold mb-1">Envoyez votre reçu de virement</p>
            <p className="text-sm text-gray-600 mb-3">Photo, capture d'écran ou PDF (JPG, PNG, WEBP, PDF)</p>
            <input ref={proofInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,application/pdf,.pdf"
              className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button type="button" onClick={() => proofInputRef.current?.click()}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <FiUpload size={15} /> {proofFile ? 'Changer le fichier' : 'Choisir le reçu'}
              </button>
              {proofFile && <span className="text-sm text-gray-700 truncate max-w-[220px]">{proofFile.name}</span>}
              <button type="button" disabled={!proofFile || proofSending} onClick={sendProof}
                className="px-5 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
                <FiCheckCircle size={15} /> {proofSending ? 'Envoi...' : 'Envoyer le reçu'}
              </button>
            </div>
          </div>

          <p className="mt-4 text-xs text-gray-500">
            Après vérification du virement par notre équipe, votre commande sera confirmée et vos coupons envoyés par email.
          </p>

          <button type="button" onClick={() => navigate(virementInfo.hasSession ? '/dashboard/orders' : '/')}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline">
            Envoyer le reçu plus tard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8">Finaliser la commande</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Formulaire */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Informations client</h2>

              {(!user || !user.email) ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 -mt-2 mb-2">
                    Pas besoin de créer un compte. Entrez votre email : votre compte sera créé
                    automatiquement et un email vous sera envoyé pour définir votre mot de passe.
                  </p>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Nom complet</label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Votre nom"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Vos vouchers et identifiants seront envoyés à cette adresse
                    </p>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Téléphone <span className="text-red-500">*</span></label>
                    <input
                      type="tel"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="06 00 00 00 00"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Vous avez déjà un compte ?{' '}
                    <button type="button" onClick={() => navigate('/login')} className="text-blue-600 font-medium underline">
                      Se connecter
                    </button>
                  </p>
                </div>
              ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Nom complet</label>
                  <input
                    type="text"
                    value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={user?.email ?? ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Les vouchers seront envoyés à cette adresse
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={user?.phone ?? ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                  {/* ✅ MESSAGE SI PAS DE TÉLÉPHONE */}
                  {(!user?.phone || user.phone.trim() === '') && (
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Ajoutez votre téléphone dans votre profil
                    </p>

                  )}
                </div>
              </div>
              )}
            </div>

            {/* Adresse de livraison (produits physiques) */}
            {hasPhysical && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-1">Adresse de livraison</h2>
                <p className="text-sm text-gray-500 mb-4">Sans le nom complet, le transporteur pourrait ne pas être en mesure de livrer le colis.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom et nom *</label>
                    <input value={shipping.name} onChange={(e) => setShip('name', e.target.value)}
                      placeholder="Prénom et nom" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse *</label>
                    <input value={shipping.address} onChange={(e) => setShip('address', e.target.value)}
                      placeholder="Adresse" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Appartement, Suite (facultatif)</label>
                    <input value={shipping.apt} onChange={(e) => setShip('apt', e.target.value)}
                      placeholder="Appartement, Suite" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                    <input value={shipping.city} onChange={(e) => setShip('city', e.target.value)}
                      placeholder="Ville" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    {zoneCities.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">
                          Frais de livraison réduits pour ces villes :
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {zoneCities.map((z) => (
                            <button
                              key={z.name}
                              type="button"
                              onClick={() => setShip('city', z.name)}
                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                                String(shipping.city || '').trim().toLowerCase() === z.name.toLowerCase()
                                  ? 'bg-primary-600 text-white border-primary-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                              }`}
                            >
                              {z.name} · {z.fee > 0 ? `${z.fee.toFixed(2)} MAD` : 'Gratuit'}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Les autres villes utilisent le tarif standard.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                    <input value={shipping.postalCode} onChange={(e) => setShip('postalCode', e.target.value)}
                      placeholder="Code postal" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">État / Province (facultatif)</label>
                    <input value={shipping.state} onChange={(e) => setShip('state', e.target.value)}
                      placeholder="État / Province" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de téléphone *</label>
                    <input value={shipping.phone} onChange={(e) => setShip('phone', e.target.value)}
                      placeholder="Numéro de téléphone" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              </div>
            )}

            {/* Méthode paiement */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Choisissez votre mode de paiement</h2>

              <div className="space-y-4">
                <label
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                    paymentMethod === 'card'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-4"
                  />
                  <FiCreditCard className="text-primary-600 mr-3" size={24} />
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      Carte bancaire (Payzone)
                      <PaymentBrandLogos brands={CARD_BRANDS} height="h-5" />
                    </div>
                    <div className="text-sm text-gray-600">
                      Paiement sécurisé par carte bancaire. Vous serez redirigé vers la
                      plateforme de paiement sécurisée pour finaliser votre paiement.
                    </div>
                  </div>
                  <FiLock className="text-gray-400" />
                </label>

                <label
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                    paymentMethod === 'virement'
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="virement"
                    checked={paymentMethod === 'virement'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="mr-4"
                  />
                  <FaUniversity className="text-primary-600 mr-3" size={24} />
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      Virement bancaire
                      {/* Le libellé vient des Réglages (Banque), pas du code :
                          changer de banque ne doit pas demander une livraison. */}
                      <PaymentBrandLogos
                        brands={[{ slug: 'bank', label: bank?.bankName || 'Banque' }]}
                        height="h-5"
                      />
                    </div>
                    <div className="text-sm text-gray-600">
                      RIB affiché après la commande. Effectuez un virement instantané,
                      puis envoyez votre reçu pour validation.
                    </div>
                  </div>
                </label>

                {cashAvailable && total >= cashMinAmount && (
                  <label
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                      paymentMethod === 'cash'
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === 'cash'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-4"
                    />
                    <FaMoneyBillWave className="text-primary-600 mr-3" size={24} />
                    <div className="flex-1">
                      <div className="font-semibold flex items-center gap-2 flex-wrap">
                        Payer en espèces en agence
                        <PayExpressBadge size="sm" />
                      </div>
                      <div className="text-sm text-gray-600">
                        Réservez maintenant, puis payez en espèces dans l'une de ces agences.
                        Un code de paiement vous sera communiqué après la commande.
                        Présentez ce code au guichet et demandez un paiement{' '}
                        <span className="font-semibold">PAYEXPRESS</span>.
                      </div>
                      {/* Les enseignes sous le texte, et non dedans : le client
                          repère d'abord l'agence proche de chez lui. */}
                      <PaymentBrandLogos brands={CASH_AGENCY_BRANDS} className="mt-2" height="h-6" />
                    </div>
                  </label>
                )}
                {cashAvailable && cashMinAmount > 0 && total < cashMinAmount && (
                  <p className="text-xs text-gray-500 px-1">
                    Le paiement en espèces en agence est disponible à partir de {cashMinAmount.toFixed(2)} MAD.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Résumé */}
          <div>
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Résumé</h2>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-sm text-primary-600 hover:text-primary-700 font-semibold"
                >
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-2">
                {selectedItems.length} article{selectedItems.length > 1 ? 's' : ''} sélectionné
                {selectedItems.length > 1 ? 's' : ''} {' / '}
                {cartItems.length} au total
              </p>

              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item._localKey} className="flex space-x-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(item._localKey)}
                      onChange={() => toggleItemSelection(item._localKey)}
                      className="mr-2"
                    />
                    <img
                      src={item.image || '/placeholder.png'}
                      alt={item.title}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{item.title}</h4>
                      <p className="text-sm text-gray-600">
                        {cartItemUnitPrice(item).toFixed(2)} MAD / unité
                        {cartItemUnitPrice(item) < (Number(item.basePrice ?? item.price) || 0) && (
                          <span className="ml-2 text-xs text-gray-400 line-through">
                            {(Number(item.basePrice ?? item.price) || 0).toFixed(2)}
                          </span>
                        )}
                      </p>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <div className="inline-flex items-center border border-gray-300 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => changeQty(item, -1)}
                            disabled={(Number(item.quantity) || 1) <= 1}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label="Diminuer la quantité"
                          >
                            <FiMinus size={14} />
                          </button>
                          <span className="px-3 py-1 text-sm font-semibold min-w-[2.25rem] text-center select-none">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => changeQty(item, 1)}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                            aria-label="Augmenter la quantité"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                        <span className="text-xs font-medium text-gray-500">
                          = {cartItemTotal(item).toFixed(2)} MAD
                        </span>
                        {/* SUPPRIMER. Decocher un article l'exclut du paiement
                            mais le laisse dans le panier : il revient a la
                            commande suivante. Il faut donc pouvoir le retirer
                            vraiment. */}
                        <button
                          type="button"
                          onClick={() => removeLine(item)}
                          className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Retirer du panier"
                          aria-label={`Retirer ${item.title} du panier`}
                        >
                          <FiTrash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total</span>
                  <span>{subtotal.toFixed(2)} MAD</span>
                </div>

                {hasPhysical && (
                  <div className="flex justify-between text-gray-600">
                    <span>
                      Frais de livraison
                      {/* Sans ville saisie, le montant affiche est le tarif par
                          DEFAUT. Le dire evite qu'un acheteur de Tanger renonce
                          en voyant 70 MAD alors qu'il n'en paiera que 17. */}
                      {!String(shipping.city || '').trim() && zoneCities.length > 0 && (
                        <span className="block text-xs text-amber-600">
                          Tarif standard — indiquez votre ville pour le tarif exact
                        </span>
                      )}
                    </span>
                    <span>{shippingFee > 0 ? `${shippingFee.toFixed(2)} MAD` : 'Gratuit'}</span>
                  </div>
                )}

                {/* Code promo */}
                {appliedPromo ? (
                  <div className="flex justify-between items-center text-green-700">
                    <span className="flex items-center gap-2">
                      Code <span className="font-mono font-semibold">{appliedPromo.code}</span>
                      <button type="button" onClick={removePromo} className="text-xs text-red-500 underline">retirer</button>
                    </span>
                    <span>appliqué</span>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Code promo"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
                    />
                    <button type="button" onClick={applyPromo} disabled={promoLoading}
                      className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">
                      {promoLoading ? '...' : 'Appliquer'}
                    </button>
                  </div>
                )}

                {discount > 0 && (
                  <div className="flex justify-between text-green-700 font-medium">
                    <span>Vous économisez</span>
                    <span>-{discount.toFixed(2)} MAD</span>
                  </div>
                )}

                <div className="border-t pt-2 flex justify-between font-bold text-xl">
                  <span>Total</span>
                  <span className="text-primary-600">{total.toFixed(2)} MAD</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitDisabled}
                className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Traitement...
                  </>
                ) : (
                  <>
                    <FiLock className="mr-2" />
                    <span>Payer {total.toFixed(2)} MAD</span>
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-3">
                <FiLock className="inline mr-1" />
                Paiement 100% sécurisé
              </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;